import { useEffect, useState } from "react";
import api from "../api";

const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const EUR = (n) => "€ " + Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DocCountsTable() {
  const [year, setYear] = useState(2026);
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState("");
  const [savedMsg, setSavedMsg] = useState("");

  const load = () => api.get(`/contabilita/doc-counts?year=${year}`, { noCache: true }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [year]); // eslint-disable-line

  if (!data) return <p className="muted">Caricamento registrazioni…</p>;

  const save = async (row, month, raw) => {
    const prev = row.counts[String(month)];
    const val = raw.trim() === "" ? null : Math.max(0, parseInt(raw, 10) || 0);
    if (val === (prev ?? null)) return;
    setSaving(`${row.company_id}-${month}`);
    try {
      const r = await api.post("/contabilita/doc-counts", { company_id: row.company_id, year, month, count: val });
      setData((d) => ({
        ...d,
        clients: d.clients.map((c) => c.company_id === row.company_id
          ? {
              ...c,
              counts: val === null
                ? Object.fromEntries(Object.entries(c.counts).filter(([k]) => k !== String(month)))
                : { ...c.counts, [String(month)]: val },
              ...(r.data.annual_margin !== undefined ? { annual_margin: r.data.annual_margin, reference_year: r.data.reference_year } : {}),
            }
          : c),
      }));
      setSavedMsg(`💾 Salvato: ${row.name} · ${MONTHS[month - 1]} ${year}${r.data.annual_margin !== undefined && r.data.annual_margin !== null ? ` · margine aggiornato: ${EUR(r.data.annual_margin)}` : ""}`);
      setTimeout(() => setSavedMsg(""), 5000);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    setSaving("");
  };

  const rows = data.clients.filter((c) => !q.trim() || (c.name || "").toLowerCase().includes(q.trim().toLowerCase()));
  const totals = MONTHS.map((_, i) => rows.reduce((s, c) => s + (Number(c.counts[String(i + 1)]) || 0), 0));

  return (
    <div className="panel" data-testid="doc-counts-panel">
      <div className="toolbar" style={{ gap: 10, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0 }}>🧾 N. registrazioni mensili</h3>
        <button className="btn sm" onClick={() => setYear((y) => y - 1)} disabled={year <= 2026} data-testid="dc-year-prev">◀</button>
        <b data-testid="dc-year-label">{year}</b>
        <button className="btn sm" onClick={() => setYear((y) => y + 1)} data-testid="dc-year-next">▶</button>
        <input placeholder="Cerca cliente…" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 220 }} data-testid="dc-search" />
        <div className="spacer" />
        {savedMsg && <span style={{ color: "#6ee7b7", fontSize: 13 }} data-testid="dc-saved-msg">{savedMsg}</span>}
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        Inserisci il numero di registrazioni fatte per ogni cliente e mese: si salva da solo appena esci dalla casella
        e il <b>margine si ricalcola automaticamente</b>. Le caselle vuote non contano (nessun rischio di doppioni con i dati {year - 1}).
      </p>
      <div className="dc-wrap">
        <table style={{ fontSize: 13 }} className="dc-sticky" data-testid="doc-counts-table">
          <thead>
            <tr>
              <th style={{ minWidth: 180 }}>Cliente</th>
              {MONTHS.map((m, i) => <th key={m} style={{ textAlign: "center", background: i === new Date().getMonth() && year === new Date().getFullYear() ? "#27407a" : undefined }}>{m}</th>)}
              <th style={{ textAlign: "center" }}>Totale {year}</th>
              {data.is_admin && <th style={{ textAlign: "right" }}>Margine</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const tot = MONTHS.reduce((s, _, i) => s + (Number(c.counts[String(i + 1)]) || 0), 0);
              return (
                <tr key={c.company_id}>
                  <td style={{ fontWeight: 600 }}>{c.name}</td>
                  {MONTHS.map((_, i) => {
                    const m = i + 1;
                    const key = `${c.company_id}-${m}`;
                    return (
                      <td key={m} style={{ padding: 2, textAlign: "center" }}>
                        <input type="number" min="0" defaultValue={c.counts[String(m)] ?? ""}
                          onBlur={(e) => save(c, m, e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                          disabled={saving === key}
                          style={{ width: 58, padding: "4px 4px", textAlign: "center", opacity: saving === key ? 0.5 : 1 }}
                          data-testid={`dc-cell-${c.company_id}-${m}`} />
                      </td>
                    );
                  })}
                  <td style={{ textAlign: "center", fontWeight: 700 }} data-testid={`dc-total-${c.company_id}`}>{tot || "—"}</td>
                  {data.is_admin && (
                    <td style={{ textAlign: "right", whiteSpace: "nowrap", color: (c.annual_margin || 0) < 0 ? "#fb7185" : "#6ee7b7" }} data-testid={`dc-margin-${c.company_id}`}>
                      {c.annual_margin !== null && c.annual_margin !== undefined ? EUR(c.annual_margin) : "—"}
                      {c.reference_year ? (
                        <span className="muted" style={{ fontSize: 10 }}> ({c.reference_year}{c.margin_period_months && c.margin_period_months < 12 ? ` fino a ${MONTHS[c.margin_period_months - 1]}` : ""})</span>
                      ) : null}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700 }}>
              <td>Totale mese</td>
              {totals.map((t, i) => <td key={i} style={{ textAlign: "center" }}>{t || "—"}</td>)}
              <td style={{ textAlign: "center" }}>{totals.reduce((a, b) => a + b, 0) || "—"}</td>
              {data.is_admin && <td />}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
